import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaFileAlt, FaEnvelope, FaUser, FaMagic, FaHistory, FaSignOutAlt, FaSync } from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import { useFirebase } from '../../contexts/FirebaseContext';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import EditProfile from '../profile/EditProfile';
import { toast } from 'react-hot-toast';

const Dashboard = () => {
  const { currentUser, logout } = useAuth();
  const { db, isInitialized, isTerminated } = useFirebase();
  const [userStats, setUserStats] = useState({
    documentsCreated: 0,
    lastActive: 'Just now'
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const mountedRef = useRef(true);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreatingIndex, setIsCreatingIndex] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [stats, setStats] = useState({
    resumes: 0,
    coverLetters: 0
  });

  // Function to format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';
    
    const date = timestamp.toDate();
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return date.toLocaleDateString();
  };

  const fetchUserStats = useCallback(async () => {
    if (!currentUser?.uid || !db || !isInitialized || isTerminated) {
      console.log('Cannot fetch stats: User not authenticated or database not ready');
      setIsLoading(false);
      return;
    }

    try {
      if (!mountedRef.current) return;
      setIsLoading(true);
      setError(null);

      // Get resumes count
      const resumesQuery = query(
        collection(db, 'resumes'),
        where('userId', '==', currentUser.uid)
      );
      const resumeSnapshot = await getDocs(resumesQuery);

      // Get cover letters count
      const coverLettersQuery = query(
        collection(db, 'coverLetters'),
        where('userId', '==', currentUser.uid)
      );
      const coverLetterSnapshot = await getDocs(coverLettersQuery);

      // Get recent activities
      const activitiesQuery = query(
        collection(db, 'activities'),
        where('userId', '==', currentUser.uid),
        orderBy('timestamp', 'desc'),
        limit(5)
      );
      const activitySnapshot = await getDocs(activitiesQuery);

      if (!mountedRef.current) return;

      setStats({
        resumes: resumeSnapshot.size,
        coverLetters: coverLetterSnapshot.size
      });

      setActivities(
        activitySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate()
        }))
      );

      const totalDocs = resumeSnapshot.size + coverLetterSnapshot.size;
      setUserStats({
        documentsCreated: totalDocs,
        lastActive: 'Just now'
      });

      setError(null);
      setRetryCount(0);
    } catch (error) {
      console.error('Error fetching user stats:', error);
      if (error.message.includes('requires an index')) {
        setError('Setting up database indexes. Please wait a moment and try again.');
      } else {
        setError('Failed to load data. Please try again.');
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
        setLoading(false);
      }
    }
  }, [currentUser, db, isInitialized, isTerminated]);

  useEffect(() => {
    mountedRef.current = true;

    if (currentUser && db && isInitialized && !isTerminated) {
      fetchUserStats();
    } else {
      setIsLoading(false);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [currentUser, db, isInitialized, isTerminated, fetchUserStats]);

  const handleRetry = () => {
    if (!isTerminated && mountedRef.current) {
      setRetryCount(0);
      fetchUserStats();
    }
  };

  const handleRefresh = () => {
    fetchUserStats();
  };

  const features = [
    {
      title: 'Create Resume',
      description: 'Build a professional resume with our AI-powered builder',
      icon: <FaFileAlt className="text-4xl" />,
      to: '/resume-builder',
      gradient: 'from-blue-600 to-indigo-600',
      hoverGradient: 'from-blue-500 to-indigo-500'
    },
    {
      title: 'Create Cover Letter',
      description: 'Craft a compelling cover letter that stands out',
      icon: <FaEnvelope className="text-4xl" />,
      to: '/cover-letter-builder',
      gradient: 'from-purple-600 to-pink-600',
      hoverGradient: 'from-purple-500 to-pink-500'
    }
  ];

  const profileStats = [
    { label: 'Documents Created', value: userStats.documentsCreated },
    { label: 'Last Active', value: userStats.lastActive },
  ];

  const handleIndexCreation = async () => {
    setIsCreatingIndex(true);
    try {
      // Create a simple query that will trigger index creation
      const activitiesRef = collection(db, 'activities');
      const simpleQuery = query(
        activitiesRef,
        where('userId', '==', currentUser.uid),
        orderBy('timestamp', 'desc'),
        limit(1)
      );

      // Try to execute the query which will trigger index creation
      await getDocs(simpleQuery);
      
      // Wait a moment for the index to be created
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Retry fetching activities
      await fetchUserStats();
      setError(null);
    } catch (error) {
      console.error('Error creating index:', error);
      if (error.message.includes('requires an index')) {
        // If we still get the index error, provide the manual link
        const indexUrl = error.message.split('here: ')[1];
        window.open(indexUrl, '_blank');
        setError('Please click "Create Index" in the new tab, then try again.');
      } else {
        setError('Failed to create index automatically. Please try again.');
      }
    } finally {
      setIsCreatingIndex(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleEditProfile = () => {
    setShowEditProfile(true);
  };

  const handleCloseEditProfile = () => {
    setShowEditProfile(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-4">
            Welcome back, {currentUser?.displayName || currentUser?.email?.split('@')[0]}
          </h1>
          <p className="text-gray-400">
            Create and manage your professional documents with AI assistance
          </p>
          {error && (
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
              <div className="flex items-center justify-between">
                <span>{error}</span>
                {error.includes('index') ? (
                  <button
                    onClick={handleIndexCreation}
                    disabled={isCreatingIndex}
                    className="flex items-center gap-2 px-3 py-1 rounded-md bg-blue-500/20 hover:bg-blue-500/30 transition-colors"
                  >
                    <FaSync className={isCreatingIndex ? 'animate-spin' : ''} />
                    {isCreatingIndex ? 'Creating Index...' : 'Create Index'}
                  </button>
                ) : (
                  <button
                    onClick={handleRetry}
                    className="flex items-center gap-2 px-3 py-1 rounded-md bg-red-500/20 hover:bg-red-500/30 transition-colors"
                  >
                    <FaSync className={isLoading ? 'animate-spin' : ''} />
                    Retry
                  </button>
                )}
              </div>
            </div>
          )}
          {isLoading && (
            <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg text-blue-400">
              Loading your documents...
            </div>
          )}
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - User Profile */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-6">
              {/* Profile Card */}
              <div className="text-center">
                <div className="relative w-24 h-24 mx-auto mb-4">
                  <img
                    src={currentUser?.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${currentUser?.email}`}
                    alt="Profile"
                    className="rounded-full border-2 border-blue-500/30"
                  />
                  <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-900"></div>
                </div>
                <h2 className="text-xl font-semibold text-white mb-1">
                  {currentUser?.displayName || currentUser?.email?.split('@')[0]}
                </h2>
                <p className="text-gray-400 text-sm mb-4">{currentUser?.email}</p>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {profileStats.map((stat, index) => (
                    <div key={index} className="bg-gray-800/50 rounded-xl p-3">
                      <div className="text-2xl font-bold text-blue-400">{stat.value}</div>
                      <div className="text-xs text-gray-400">{stat.label}</div>
                    </div>
                  ))}
                </div>

                {/* Profile Actions */}
                <div className="space-y-3">
                  <button
                    onClick={handleEditProfile}
                    className="w-full btn btn-primary flex items-center justify-center gap-2 mb-2"
                  >
                    <FaUser className="text-lg" /> Edit Profile
                  </button>
                  <button 
                    onClick={logout}
                    className="w-full px-4 py-2 rounded-lg bg-red-600/10 text-red-400 border border-red-500/20 hover:bg-red-600/20 transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <FaSignOutAlt className="text-sm" />
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Creation Cards */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <Link
                key={index}
                to={feature.to}
                className={`group relative overflow-hidden bg-gradient-to-br ${feature.gradient} hover:${
                  feature.hoverGradient
                } rounded-2xl p-6 transition-all duration-500 transform hover:-translate-y-1 hover:shadow-2xl`}
              >
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10">
                  <div className="mb-4 text-white/90">{feature.icon}</div>
                  <h3 className="text-2xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-white/80">{feature.description}</p>
                  <div className="mt-6 inline-flex items-center text-white/90">
                    <span>Get Started</span>
                    <svg
                      className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}

            {/* Recent Activity Card */}
            <div className="md:col-span-2 bg-gray-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <FaHistory className="text-blue-400" />
                  Recent Activity
                </h3>
                <button 
                  onClick={handleRefresh}
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-2"
                >
                  <FaSync className={loading ? 'animate-spin' : ''} />
                  Refresh
                </button>
              </div>
              <div className="space-y-4">
                {loading ? (
                  <div className="text-center text-gray-400 py-4">
                    Loading activities...
                  </div>
                ) : activities.length === 0 ? (
                  <div className="text-center text-gray-400 py-4">
                    No recent activities
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activities.map((activity) => (
                      <div
                        key={activity.id}
                        className="flex items-center justify-between p-4 bg-base-300/50 rounded-lg hover:bg-base-300 transition-colors"
                      >
                        <div>
                          <h3 className="font-medium">{activity.title}</h3>
                          <p className="text-sm text-gray-500">
                            {activity.action} • {formatDate(activity.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditProfile && (
        <EditProfile onClose={handleCloseEditProfile} />
      )}
    </div>
  );
};

export default Dashboard;
